import { useEffect, useState, useRef, useMemo } from 'react'
import { useAccount, useNetwork, useWalletClient, usePublicClient } from 'wagmi'
// eslint-disable-next-line import/no-unresolved
import { useSelector } from 'react-redux'
import { State } from '~/state/types'
import { CHAIN_ID, SUPPORTED_CHAINS } from '~/config/constants/chains'
import { deurlChainId } from '~/utils'


/**
 * Provides a web3 provider with or without user's signer
 * Recreate web3 instance only if the provider change
 */
// const useActiveWeb3React = (): Web3ReactContextInterface> => {
//   const appChainId = useSelector((state: State) => state.network.data.chainId)
//   const appProvider = getProvider(appChainId)

//   const refEth = useRef(library)

//   const [provider, setProvider] = useState(library || appProvider)

//   useEffect(() => {
//     if (library !== refEth.current) {
//       setProvider(library || appProvider)
//       refEth.current = library
//     }
//   }, [library, appProvider])

//   return { library: provider, chainId: chainId || appChainId, ...web3React }
// }

const useActiveWeb3React = () => {
    const { chain } = useNetwork()
    const publicClient = usePublicClient()
    const { data: library } = useWalletClient()
    const { address: account, isConnected } = useAccount()

    return useMemo(() => {

        const fallbackChain = SUPPORTED_CHAINS
          .includes(publicClient.chain.id) ? publicClient.chain.id : CHAIN_ID.SHIMMER

        const chainId = chain && SUPPORTED_CHAINS.includes(chain.id) ? chain.id : fallbackChain

        return {
            chainId: chainId,
            library,
            account,
            publicClient,
            isConnected
        }

    }, [publicClient, chain, account, library])
}

export const useActiveChainId = () => {
    const { chain } = useNetwork()
    const publicClient = usePublicClient()

    return useMemo(() => {

        const fallbackChain = SUPPORTED_CHAINS
          .includes(publicClient.chain.id) ? publicClient.chain.id : CHAIN_ID.SHIMMER

        const chainId = chain && SUPPORTED_CHAINS.includes(chain.id) ? chain.id : fallbackChain

        return {
            chainId: chainId,
        }

    }, [publicClient, chain])
}

export const useNoAccountOrWrongChain = (chainId?: number) => {
    const { address: account } = useAccount()
    const { chainId: activeChainId } = useActiveWeb3React()
    const { chain } = useNetwork()
    const paramsChainId = useChainIdByLocaction()
    let publicId = chainId || paramsChainId || chain?.id

    return { noAccountOrWrongChain: !account || publicId !== activeChainId }
}

export const usePublicOrActiveChainId = () => {
    const { chainId: activeChainId } = useActiveChainId()
    const { address: account } = useAccount()
    const paramsChainId = useChainIdByLocaction()
    return useMemo(() => {
        if (!paramsChainId) {
            return {
                chainId: activeChainId,
                account,
                noAccountOrWrongChain: !account
            }
        }
        return {
            chainId: paramsChainId,
            account,
            noAccountOrWrongChain: !account || paramsChainId !== activeChainId
        }
    }, [paramsChainId, activeChainId, account])
}


export const getChainIdFromLocation = (pathname): number => {
    const regex = /:([^/]+)/
    const matched = regex.exec(pathname)
    if (matched) {
        return deurlChainId(matched[1])
    }
    return null
}

/**
 * We MUST "encode" the chainId because we can not use the useParams hooks
 * as it will fail on components/functions/hooks which are not under <Router> like Multicall Updater
 *
 * So we need a way to parse the chain Id from the current window.location. We use a simple "_" as prefix
 * for the real chainId in order to parse it.
 */
export const useChainIdByLocaction = (): number | undefined => {
    const pathname = window.location.pathname
    return useMemo(() => {
        const regex = /:([^/]+)/
        const matched = regex.exec(pathname)
        if (matched) {
            return deurlChainId(matched[1])
        }
        return null
    }, [pathname])
}

export default useActiveWeb3React
